import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDateTime, fmtISODate, fmtDate } from "@/lib/dates";
import {
  Clock,
  RefreshCcw,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Activity,
  Users,
  TimerReset,
} from "lucide-react";

const TYPE_LABEL = {
  late_clock_in: "Late clock-in",
  early_clock_out: "Early clock-out",
  overtime: "Overtime",
};

const SEVERITY_STYLES = {
  low: "bg-amber-50 border-amber-200 text-amber-900",
  medium: "bg-orange-50 border-orange-200 text-orange-900",
  high: "bg-rose-50 border-rose-200 text-rose-900",
};

function formatDateTime(value) {
  if (!value) return "—";
  return fmtDateTime(value) || value;
}

function downloadCsv(filename, rows) {
  if (!rows || rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const csv = [headers.join(",")]
    .concat(
      rows.map((row) =>
        headers
          .map((h) => {
            const v = row[h];
            const s = v == null ? "" : String(v);
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
          })
          .join(","),
      ),
    )
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function JibbleDashboard() {
  const [status, setStatus] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [anomalies, setAnomalies] = useState([]);
  const [filter, setFilter] = useState("open");
  const [typeFilter, setTypeFilter] = useState("all");
  const [days, setDays] = useState(7);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [loadErr, setLoadErr] = useState("");

  const load = async () => {
    setLoadErr("");
    try {
      const [s, d, a] = await Promise.all([
        api.get("/jibble/status"),
        api.get(`/jibble/dashboard?days=${days}`),
        api.get(
          `/jibble/anomalies${filter === "all" ? "" : `?status_filter=${filter}`}`,
        ),
      ]);
      setStatus(s.data);
      setDashboard(d.data);
      setAnomalies(a.data.items || []);
    } catch (e) {
      setLoadErr(
        e?.response?.data?.detail || "Failed to load Jibble dashboard data",
      );
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, filter]);

  const triggerSync = async () => {
    setSyncing(true);
    setSyncMsg("");
    try {
      const { data } = await api.post("/jibble/sync", {});
      setSyncMsg(
        `Synced ${data.entries_synced} entries · ${data.anomalies_detected} anomalies (window ${fmtDate(data.from)} → ${fmtDate(data.to)})`,
      );
      await load();
    } catch (e) {
      setSyncMsg(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const [resolveId, setResolveId] = useState(null);
  const [resolveNote, setResolveNote] = useState("");
  const [resolveErr, setResolveErr] = useState("");

  const openResolve = (id) => {
    setResolveId(id);
    setResolveNote("");
    setResolveErr("");
  };

  const submitResolve = async () => {
    if (!resolveId) return;
    setResolveErr("");
    try {
      await api.post(`/jibble/anomalies/${resolveId}/resolve`, { note: resolveNote });
      setResolveId(null);
      setResolveNote("");
      await load();
    } catch (e) {
      setResolveErr(e?.response?.data?.detail || "Failed to resolve");
    }
  };

  const filteredAnoms =
    typeFilter === "all"
      ? anomalies
      : anomalies.filter((a) => a.type === typeFilter);

  const exportAnomaliesCsv = () => {
    if (filteredAnoms.length === 0) return;
    downloadCsv(
      `jibble-anomalies-${fmtISODate(new Date())}.csv`,
      filteredAnoms.map((a) => ({
        type: a.type,
        severity: a.severity,
        staff: a.staff_name,
        minutes: a.minutes,
        scheduled_start: a.scheduled_start,
        scheduled_end: a.scheduled_end,
        actual_start: a.actual_start,
        actual_end: a.actual_end,
        status: a.status,
        created_at: a.created_at,
      })),
    );
  };

  if (loadErr) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-md p-4 text-rose-800 text-sm" data-testid="jibble-load-error">
        {loadErr}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="jibble-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Clock className="h-5 w-5 text-[#0D7377]" /> Time &amp; Attendance
          </h1>
          <p className="text-sm text-slate-600">
            Jibble-synced clock events with anomaly detection (late, early, overtime) and variance reporting.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="jibble-window-select"
            className="text-sm border border-slate-300 rounded-md px-2 py-2"
          >
            {[1, 7, 14, 30, 60, 90].map((d) => (
              <option key={d} value={d}>
                Last {d} day{d === 1 ? "" : "s"}
              </option>
            ))}
          </select>
          <button
            onClick={triggerSync}
            disabled={syncing}
            data-testid="jibble-sync-button"
            className="inline-flex items-center gap-2 bg-[#0D7377] hover:bg-[#0A5C5F] disabled:opacity-60 text-white px-4 py-2 rounded-md font-medium min-h-[40px]"
          >
            <RefreshCcw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync from Jibble"}
          </button>
        </div>
      </div>

      {/* Status banner */}
      {status && !status.ok && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-amber-900 text-sm" data-testid="jibble-status-warning">
          Jibble integration: {status.message || "not yet authenticated"}
        </div>
      )}
      {syncMsg && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-emerald-900 text-sm" data-testid="jibble-sync-message">
          {syncMsg}
        </div>
      )}

      {/* KPI cards */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard
            icon={Users}
            label="Linked staff"
            value={dashboard.totals.linked_staff}
            testId="kpi-linked-staff"
          />
          <KpiCard
            icon={Activity}
            label="Entries"
            value={dashboard.totals.total_entries}
            testId="kpi-entries"
          />
          <KpiCard
            icon={AlertTriangle}
            label="Open anomalies"
            value={dashboard.totals.open_anomalies}
            tone={dashboard.totals.open_anomalies > 0 ? "warn" : "ok"}
            testId="kpi-open-anomalies"
          />
          <KpiCard
            icon={TimerReset}
            label="Variance"
            value={`${(dashboard.totals.total_actual_hours - dashboard.totals.total_scheduled_hours).toFixed(1)}h`}
            sub={`Scheduled ${dashboard.totals.total_scheduled_hours.toFixed(1)}h · Actual ${dashboard.totals.total_actual_hours.toFixed(1)}h`}
            testId="kpi-variance"
          />
        </div>
      )}

      {/* Per-staff variance table */}
      {dashboard && dashboard.per_staff && dashboard.per_staff.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 overflow-auto">
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-3">Per-staff variance &amp; compliance</h2>
          <table className="w-full text-sm" data-testid="jibble-variance-table">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left py-2 px-3">Staff</th>
                <th className="text-right py-2 px-3">Scheduled (h)</th>
                <th className="text-right py-2 px-3">Actual (h)</th>
                <th className="text-right py-2 px-3">Variance (h)</th>
                <th className="text-right py-2 px-3">Anomalies</th>
                <th className="text-right py-2 px-3">Compliance</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.per_staff.map((r) => (
                <tr key={r.staff_id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 px-3 font-medium text-slate-900">{r.staff_name}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{r.scheduled_hours.toFixed(1)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{r.actual_hours.toFixed(1)}</td>
                  <td className={`py-2 px-3 text-right tabular-nums ${r.variance_hours < -0.25 ? "text-rose-700" : r.variance_hours > 0.25 ? "text-orange-700" : "text-slate-700"}`}>
                    {r.variance_hours > 0 ? "+" : ""}{r.variance_hours.toFixed(1)}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <span className="text-xs text-slate-600">
                      L:{r.anomalies.late_clock_in} · E:{r.anomalies.early_clock_out} · O:{r.anomalies.overtime}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <span
                      className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-md ${
                        r.compliance_pct >= 90
                          ? "bg-emerald-50 text-emerald-700"
                          : r.compliance_pct >= 70
                          ? "bg-amber-50 text-amber-700"
                          : "bg-rose-50 text-rose-700"
                      }`}
                    >
                      {r.compliance_pct.toFixed(0)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Anomalies list */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-orange-600" /> Anomalies
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              data-testid="jibble-anomaly-type-filter"
              className="text-sm border border-slate-300 rounded-md px-2 py-1.5"
            >
              <option value="all">All types</option>
              <option value="late_clock_in">Late clock-in</option>
              <option value="early_clock_out">Early clock-out</option>
              <option value="overtime">Overtime</option>
            </select>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="jibble-anomaly-status-filter"
              className="text-sm border border-slate-300 rounded-md px-2 py-1.5"
            >
              <option value="open">Open</option>
              <option value="resolved">Resolved</option>
              <option value="all">All</option>
            </select>
            <button
              onClick={exportAnomaliesCsv}
              disabled={filteredAnoms.length === 0}
              data-testid="jibble-anomaly-export"
              className="text-sm bg-slate-100 hover:bg-slate-200 disabled:opacity-50 px-3 py-1.5 rounded-md"
            >
              Export CSV
            </button>
          </div>
        </div>

        {filteredAnoms.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center" data-testid="jibble-anomaly-empty">
            No anomalies to display. Sync from Jibble after staff have clocked in/out to detect issues.
          </p>
        ) : (
          <ul className="space-y-2" data-testid="jibble-anomaly-list">
            {filteredAnoms.map((a) => (
              <li
                key={a.id}
                data-testid={`jibble-anomaly-${a.id}`}
                className={`border rounded-md p-3 ${
                  a.status === "resolved"
                    ? "bg-slate-50 border-slate-200 text-slate-600"
                    : SEVERITY_STYLES[a.severity] || "bg-slate-50 border-slate-200"
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold flex items-center gap-2">
                      {TYPE_LABEL[a.type] || a.type}
                      <span className="text-xs font-normal opacity-70">· {a.staff_name}</span>
                    </p>
                    <p className="text-xs mt-0.5">{a.message}</p>
                    <p className="text-[11px] mt-1 opacity-80">
                      Scheduled {formatDateTime(a.scheduled_start)} → {formatDateTime(a.scheduled_end)} <ChevronRight className="inline h-3 w-3" /> Actual {formatDateTime(a.actual_start)} → {formatDateTime(a.actual_end)}
                    </p>
                    {a.status === "resolved" && (
                      <p className="text-[11px] mt-1 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Resolved by {a.resolved_by_name} — {a.resolution_note || "no note"}
                      </p>
                    )}
                  </div>
                  {a.status !== "resolved" && resolveId !== a.id && (
                    <button
                      type="button"
                      onClick={() => openResolve(a.id)}
                      data-testid={`jibble-anomaly-resolve-${a.id}`}
                      className="text-xs bg-white hover:bg-slate-50 text-slate-900 px-3 py-1.5 rounded-md border border-slate-300 font-semibold shadow-sm"
                    >
                      Resolve
                    </button>
                  )}
                </div>
                {resolveId === a.id && (
                  <div className="mt-3 pt-3 border-t border-current/20 space-y-2" data-testid={`jibble-resolve-form-${a.id}`}>
                    <label className="text-xs font-semibold uppercase tracking-wider opacity-80">Resolution note</label>
                    <textarea
                      autoFocus
                      value={resolveNote}
                      onChange={(e) => setResolveNote(e.target.value)}
                      placeholder="e.g. approved overtime, traffic delay, schedule mistake"
                      rows={2}
                      className="w-full px-3 py-2 text-sm rounded-md border border-slate-300 bg-white text-slate-900 focus:border-[#0D7377] focus:ring-2 focus:ring-[#0D7377]/20 outline-none"
                      data-testid={`jibble-resolve-note-${a.id}`}
                    />
                    {resolveErr && <p className="text-xs text-rose-700">{resolveErr}</p>}
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => { setResolveId(null); setResolveNote(""); setResolveErr(""); }}
                        data-testid={`jibble-resolve-cancel-${a.id}`}
                        className="text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={submitResolve}
                        data-testid={`jibble-resolve-confirm-${a.id}`}
                        className="text-xs px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold inline-flex items-center gap-1"
                      >
                        <CheckCircle2 className="h-3 w-3" /> Mark resolved
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tone = "default", testId }) {
  const toneCls =
    tone === "warn"
      ? "border-orange-200 bg-orange-50"
      : tone === "ok"
        ? "border-emerald-200 bg-emerald-50"
        : "border-slate-200 bg-white";
  return (
    <div
      data-testid={testId}
      className={`rounded-lg border ${toneCls} p-4 shadow-sm`}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className="font-heading text-2xl font-bold text-slate-900 mt-1">
        {value}
      </p>
      {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}
